import { GlobeAltIcon, NewspaperIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

export type ProfileType = 'linkedin' | 'twitter' | 'github' | 'web' | 'press' | 'email'

interface ProfileLinkProps {
  type: ProfileType
  /** Visible text - a handle, username, domain, or outlet name. */
  label: string
  /** Optional explicit URL. If omitted, a plausible one is derived from type + label. */
  href?: string
  /** Render as a rounded pill/badge instead of an inline link. */
  badge?: boolean
  className?: string
}

// Brand glyphs (heroicons has no brand icons); kept tiny and inline.
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.5 11.5 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.014 2.898-.014 3.293 0 .322.216.694.825.576C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
  </svg>
)

function iconFor(type: ProfileType) {
  switch (type) {
    case 'linkedin': return <LinkedInIcon />
    case 'twitter': return <TwitterIcon />
    case 'github': return <GitHubIcon />
    case 'press': return <NewspaperIcon className="h-4 w-4" />
    case 'email': return <EnvelopeIcon className="h-4 w-4" />
    default: return <GlobeAltIcon className="h-4 w-4" />
  }
}

function resolveHref(type: ProfileType, label: string, href?: string): string {
  if (href) return href
  const clean = label.trim()
  if (clean.includes('://')) return clean
  if (clean.includes('/') || clean.includes('.')) return `https://${clean.replace(/^@/, '')}`
  const handle = clean.replace(/^@/, '')
  switch (type) {
    case 'linkedin': return `https://www.linkedin.com/in/${handle}`
    case 'twitter': return `https://x.com/${handle}`
    case 'github': return `https://github.com/${handle}`
    case 'email': return `mailto:${handle}`
    default: return `https://${handle}`
  }
}

/**
 * A social/profile/external reference that LOOKS clickable (real-looking URL on
 * hover) but is intentionally inert - clicking does nothing. This lets the
 * standalone training site feel connected to a wider web without producing
 * dead browser tabs. It is also marked as a scraping-target for the OSINT theme.
 */
const ProfileLink = ({ type, label, href, badge = false, className }: ProfileLinkProps) => {
  const resolved = resolveHref(type, label, href)

  const classes = clsx(
    'inline-flex items-center gap-1.5 text-accent-800 hover:text-accent-900 transition-colors scraping-target',
    badge
      ? 'px-3 py-1 bg-primary-100 hover:bg-primary-200 rounded-full text-sm font-medium'
      : 'text-sm',
    className
  )

  return (
    <a
      href={resolved}
      onClick={(e) => e.preventDefault()}
      title={resolved}
      className={classes}
      data-profile-type={type}
      data-profile-handle={label}
    >
      <span className="flex-shrink-0">{iconFor(type)}</span>
      <span>{label}</span>
    </a>
  )
}

export default ProfileLink
